import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import type {
  CategoryFilterState,
  CategoryFilterUpdate,
} from '@eliadmin/core/src/components/categories/category-query-state';

/** One selected filter value: the chip label and the `setFilter` update that removes it. */
export type ActiveFilter = { key: string; label: string; remove: CategoryFilterUpdate };

type Labels = {
  /** Price chip text, e.g. `(min, max) => \`Cena: ${min} – ${max} €\`` (bounds are numbers, 0 = open). */
  price: (min: number, max: number) => string;
  /** „Iba v akcii“ chip, only when the discounted filter is on. */
  discounted: string;
};

const byId = <T extends { id: number }>(items: T[] | null | undefined, id: number) =>
  items?.find(item => item.id === id);

/**
 * Selected filters of a category in the order of the Figma chips (manufacturers, availabilities,
 * statuses, attributes, price). Values the category no longer offers are skipped – they cannot be
 * named and the API ignores them.
 */
export function activeFilters(
  category: Category,
  filter: CategoryFilterState,
  labels: Labels,
): ActiveFilter[] {
  const chips: ActiveFilter[] = [];

  for (const id of filter.subCategories) {
    const item = byId(category.sub_categories, id);
    if (item) chips.push({ key: `sub-${id}`, label: item.name, remove: { subCategory: id } });
  }
  for (const id of filter.manufacturers) {
    const item = byId(category.manufacturers, id);
    if (item) chips.push({ key: `man-${id}`, label: item.name, remove: { manufacturer: id } });
  }
  for (const id of filter.availabilities) {
    const item = byId(category.availabilities, id);
    if (item) chips.push({ key: `av-${id}`, label: item.name, remove: { availability: id } });
  }
  for (const id of filter.statuses) {
    const item = byId(category.statuses, id);
    if (item) chips.push({ key: `st-${id}`, label: item.name, remove: { status: id } });
  }
  for (const selected of filter.attributes) {
    const attribute = byId(category.attributes, selected.id);
    // A slider selects a range of values – one chip „Dĺžka: 20 – 50“, removed as a whole.
    if (attribute?.type.startsWith('slider/')) {
      const values = (attribute.values ?? [])
        .filter(value => selected.values.includes(value.id))
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      if (values.length) {
        const range =
          values.length > 1 ? `${values[0].name} – ${values.at(-1)?.name}` : values[0].name;
        chips.push({
          key: `attr-${attribute.id}`,
          label: `${attribute.name}: ${range}`,
          remove: { attributes: filter.attributes.filter(item => item.id !== attribute.id) },
        });
      }
      continue;
    }
    for (const valueId of selected.values) {
      const value = byId(attribute?.values, valueId);
      if (attribute && value) {
        chips.push({
          key: `attr-${attribute.id}-${value.id}`,
          label: value.name,
          remove: { selectedValue: { attribute, value } },
        });
      }
    }
  }
  if (filter.price_min > 0 || filter.price_max > 0) {
    chips.push({
      key: 'price',
      label: labels.price(filter.price_min, filter.price_max),
      remove: { price_min: 0, price_max: 0 },
    });
  }
  if (filter.discounted) {
    chips.push({ key: 'discounted', label: labels.discounted, remove: { discounted: false } });
  }
  return chips;
}
